import React from 'react';
import {classes, style} from './doppe-viewer-horizontal-button.st.css';
import type {OmitStrict, Values} from "@wix/devzai-utils-common";
import {HorizontalButton, reactCreateMemo} from "@wix/devzai-utils-react";
import {DoppeViewerSpinner, DoppeViewerSpinnerSize} from "../doppe-viewer-spinner/doppe-viewer-spinner";

export const DoppeViewerHorizontalButtonSize = {
    XS: 'xs',
    Small: 'small',
    MediumSmall: 'mediumSmall',
    Medium: 'medium',
    Large: 'large',
} as const;

export type DoppeViewerHorizontalButtonSize = Values<typeof DoppeViewerHorizontalButtonSize>;

export const DoppeViewerHorizontalButtonPriority = {
    Primary: 'primary',
    Secondary: 'secondary',
} as const;

export type DoppeViewerHorizontalButtonPriority = Values<typeof DoppeViewerHorizontalButtonPriority>;

export const DoppeViewerHorizontalButtonSkin = {
    None: 'none',
    Default: 'default',
    Light: 'light',
} as const;

export type DoppeViewerHorizontalButtonSkin = Values<typeof DoppeViewerHorizontalButtonSkin>;

export const DoppeViewerHorizontalButtonRoundness = {
    Full: 'full',
    Medium: 'medium',
    None: 'none',
} as const;

export type DoppeViewerHorizontalButtonRoundness = Values<typeof DoppeViewerHorizontalButtonRoundness>;

/**
 * Resolves the button size based on layout size and viewport.
 * 
 * Button Size Logic:
 * - Layout Size: Small → Mobile: XS, Desktop: Small
 * - Layout Size: Medium → Mobile: MediumSmall, Desktop: Medium
 * 
 * @param layoutSize - The grid layout size (Small or Medium)
 * @param isDesktop - Whether the current viewport is desktop (Wide layout)
 * @returns The appropriate button size
 */
export function doppeViewerHorizontalButtonSizeResolveFromLayoutSize(
    layoutSize: 'Small' | 'Medium',
    isDesktop: boolean
): DoppeViewerHorizontalButtonSize {
    if (layoutSize === 'Small') {
        return isDesktop ? DoppeViewerHorizontalButtonSize.Medium : DoppeViewerHorizontalButtonSize.Small;
    } else {
        // Medium layout size
        return DoppeViewerHorizontalButtonSize.Medium;
    }
}

export function doppeViewerHorizontalButtonSizeResolveForRowLayout(
    isDesktop: boolean
): DoppeViewerHorizontalButtonSize {
    return isDesktop ? DoppeViewerHorizontalButtonSize.Medium : DoppeViewerHorizontalButtonSize.XS;
}

export const DoppeViewerHorizontalButton = reactCreateMemo(function DoppeViewerHorizontalButton (props: DoppeViewerHorizontalButton.Props) {
    const {
        size = DoppeViewerHorizontalButtonSize.Medium,
        priority = DoppeViewerHorizontalButtonPriority.Primary,
        skin = DoppeViewerHorizontalButtonSkin.None,
        roundness = DoppeViewerHorizontalButtonRoundness.Full,
        textual = false,
        withUnderline = false,
        isLoading = false,

        className,
        ...htmlAttributes
    } = props;

    return (
        <HorizontalButton
            {...htmlAttributes}
            size={size}
            loader={isLoading ? (
                <DoppeViewerSpinner
                    className={style(classes.spinner, {})}
                    size={DoppeViewerSpinnerSize.Tiny}
                />
            ) : undefined}
            className={style(classes.root, {size: size, priority: priority, skin: skin, roundness: roundness, textual: textual, withUnderline: withUnderline}, className)}
        />
    );
});

export namespace DoppeViewerHorizontalButton {

    export interface Props extends OmitStrict<HorizontalButton.Props<DoppeViewerHorizontalButtonSize>, 'loader' | 'size'> {
        size?: DoppeViewerHorizontalButtonSize;
        /**
         * Priority variant for the button.
         * @default 'primary'
         */
        priority?: DoppeViewerHorizontalButtonPriority;
        /**
         * Skin variant for the button.
         * - None: No styling (only sizing) - for backward compatibility
         * - Default: Full styling with colors, hover states, etc.
         * @default 'none'
         */
        skin?: DoppeViewerHorizontalButtonSkin;
        /**
         * Border roundness variant for the button.
         * - Full: Full pill-shaped roundness (current size-based radius)
         * - Medium: 8px border-radius
         * - None: 0px border-radius (square corners)
         * Only applies when textual is false.
         * @default 'full'
         */
        roundness?: DoppeViewerHorizontalButtonRoundness;
        /**
         * Whether to render the button in textual mode (link-like appearance).
         * @default false
         */
        textual?: boolean;
        /**
         * Whether to show underline on the textual button.
         * Only applies when textual is true.
         * @default false
         */
        withUnderline?: boolean;
        /**
         * Whether the button is in a loading state.
         * When true, displays a spinner and disables the button.
         * @default false
         */
        isLoading?: boolean;
    }
}
